import { useEffect, useMemo, useState } from 'react'
import { readError } from '../lib/errors'
import {
  fetchThreeStepRoomByCode, fetchThreeStepRoomSnapshot,
  subscribeToThreeStepRoom,
  type ThreeStepRoomSnapshot,
} from '../games/three-step/api/rooms'
import { RoomQR } from '../components/RoomQR'
import { TvJoinBadge } from './TvJoinBadge'
import { useT } from '../i18n'
import { useTNodes } from '../i18n/nodes'

interface Props { code: string }

/**
 * Three Step Solution — TV display.
 *
 * Strictly read-only. The phone-side host drives every state transition;
 * the TV just subscribes to realtime and renders the current phase as a
 * big-screen view. Mirrors `ColorfulTvRoomScreen` in shape (subscribe,
 * gate by phase, render phase-specific component).
 */
export function ThreeStepTvRoomScreen({ code }: Props) {
  const t = useT()
  const [snap, setSnap] = useState<ThreeStepRoomSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  // Initial lookup by code → fetch snapshot → subscribe.
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const room = await fetchThreeStepRoomByCode(code)
        if (cancelled) return
        if (!room) { setNotFound(true); setLoading(false); return }
        const s = await fetchThreeStepRoomSnapshot(room.id)
        if (cancelled) return
        setSnap(s)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [code])

  // Realtime — refetch the whole snapshot on any change. The room is
  // small (≤6 players, a handful of rows per round), so re-pulling on
  // every event is dramatically simpler than splice-merging.
  useEffect(() => {
    if (!snap) return
    const roomId = snap.room.id
    const unsub = subscribeToThreeStepRoom(roomId, () => {
      fetchThreeStepRoomSnapshot(roomId).then(setSnap).catch(() => {})
    })
    return () => { unsub() }
  }, [snap?.room.id])

  if (notFound) {
    return <FullMessage
      title={t('Room not found')}
      body={t('No Three Step game is running under code {code}.', { code })}
    />
  }
  if (error)            return <FullMessage title={t('Error')} body={error} />
  if (loading || !snap) return <FullMessage title={t('Loading room…')} body={t('Code {code}', { code })} />

  const { room } = snap
  const status = room.status

  if (status === 'abandoned')      return <FullMessage title={t('Game ended')} body={t('The host left the room.')} />

  // The join badge rides along on every live phase so the code, QR,
  // and address never leave the screen.
  const view =
    status === 'lobby'          ? <TvLobby snap={snap} /> :
    status === 'phase1'         ? <TvPhase1 snap={snap} /> :
    status === 'phase3'         ? <TvPhase3 snap={snap} /> :
    status === 'phase4_reveal'  ? <TvPhase4Reveal snap={snap} /> :
    status === 'phase4_voting'  ? <TvPhase4Voting snap={snap} /> :
    status === 'between_rounds' ? <TvBetweenRounds snap={snap} /> :
    status === 'results'        ? <TvResults snap={snap} /> :
    <FullMessage title={t('Unknown state')} body={`status=${status}`} />

  return <>{view}<TvJoinBadge code={room.code} /></>
}

// ─── Phase views ─────────────────────────────────────────────────────

function TvLobby({ snap }: { snap: ThreeStepRoomSnapshot }) {
  const t = useT()
  const { room, players } = snap
  return (
    <TvShell roomCode={room.code}>
      <div className="text-center">
        <p className="font-display text-xl text-brand-muted uppercase tracking-[0.3em]">{t('THREE STEP SOLUTION · waiting for players')}</p>

        {/* The room code IS the headline. Big and chunky so guests
            across the room can read it off the TV in a glance. */}
        <p className="text-sm font-semibold uppercase tracking-[0.4em] text-brand-muted mt-12 mb-3">{t('Room code')}</p>
        <p className="font-display font-black text-game-three-step-dark
                      text-[12rem] leading-none tracking-[0.15em]">
          {room.code}
        </p>

        {/* Where to go on your phone — the join URL spelled out. URL
            colored like the room code so the two share the same visual
            anchor and stand out together. */}
        <p className="font-display text-2xl text-brand-text mt-10 max-w-3xl mx-auto">
          {t('On your phone, go to')}{' '}
          <span className="font-mono font-bold text-game-three-step-dark">
            games.jordandraper.com/join
          </span>
          {' '}{t('and enter the code.')}
        </p>
        <p className="font-display text-lg text-brand-muted mt-3 max-w-2xl mx-auto">{t('No account needed. Guests can play.')}</p>

        <div className="mt-10 flex justify-center">
          <RoomQR code={room.code} size={208} />
        </div>

        <PlayerStrip players={players} className="mt-12" />
      </div>
    </TvShell>
  )
}

function TvPhase1({ snap }: { snap: ThreeStepRoomSnapshot }) {
  const t = useT()
  const { room, players, progress } = snap
  // RLS hides phase1 actions from non-author clients, so the TV can't
  // count them itself. The progress IDs come from a SECURITY DEFINER
  // RPC that bypasses RLS for aggregate counts only (no content).
  const done = useMemo(
    () => new Set(progress.phase1DoneIds),
    [progress.phase1DoneIds],
  )

  return (
    <TvShell roomCode={room.code}>
      <div className="text-center">
        <p className="font-display text-2xl text-brand-muted uppercase tracking-[0.3em]">{t('Phase 1')}</p>
        <h1 className="font-display text-7xl font-black text-brand-text mt-6 max-w-4xl mx-auto">{t('Each player writes three actions')}</h1>
        <p className="font-display text-2xl text-brand-muted mt-3 max-w-3xl mx-auto">
          {t('things you do, or things that happen to you')}
        </p>
        <p className="font-display text-2xl text-brand-muted mt-8">
          {t('{n} / {total} done', { n: done.size, total: players.length })}
        </p>
        <PlayerStrip players={players} doneIds={done} className="mt-16" />
      </div>
    </TvShell>
  )
}

function TvPhase3({ snap }: { snap: ThreeStepRoomSnapshot }) {
  const t = useT()
  const tn = useTNodes()
  const { room, players, progress } = snap
  const a = room.current_action_a ?? '?'
  const b = room.current_action_b ?? '?'
  // Same RLS workaround as TvPhase1 — steps are private to the author
  // during phase3, so we read submission status through the progress RPC.
  const submittedThisRound = new Set(progress.phase3DoneIds)
  return (
    <TvShell roomCode={room.code}>
      <div className="text-center">
        <p className="font-display text-2xl text-brand-muted uppercase tracking-[0.3em]">
          {t('Round {n} · the prompt', { n: room.current_round })}
        </p>
        <h1 className="font-display text-6xl font-black text-brand-text mt-8 max-w-5xl mx-auto leading-tight">
          {tn('How do you {a} while {b} at the same time, in three steps?', {
            a: <span className="text-game-three-step-dark">{a}</span>,
            b: <span className="text-game-three-step-dark">{b}</span>,
          })}
        </h1>
        <p className="font-display text-3xl text-brand-muted mt-12">
          {t('{n} / {total} answers in', { n: submittedThisRound.size, total: players.length })}
        </p>
        <PlayerStrip players={players} doneIds={submittedThisRound} className="mt-16" />
      </div>
    </TvShell>
  )
}

function TvPhase4Reveal({ snap }: { snap: ThreeStepRoomSnapshot }) {
  const t = useT()
  const tn = useTNodes()
  const { room, players, steps } = snap
  // Order by player join_order to match the in-room app — both clients
  // index off the same `room.reveal_index`, so the orderings have to
  // line up. Previously the TV sorted by `submitted_at` (submission
  // time) while the app sorted by join_order, so the two showed
  // different players at the same reveal index.
  const ordered = players.slice().sort((a, b) => a.join_order - b.join_order)
  const idx = Math.min(room.reveal_index, ordered.length - 1)
  const author = ordered[idx]
  const current = author
    ? steps.find(s => s.player_id === author.id && s.round === room.current_round)
    : null
  const a = room.current_action_a ?? '?'
  const b = room.current_action_b ?? '?'

  if (!author) {
    return <FullMessage title={t('Reveal')} body={t('No players seated.')} />
  }

  return (
    <TvShell roomCode={room.code}>
      <div className="text-center max-w-5xl mx-auto">
        <p className="font-display text-xl text-brand-muted uppercase tracking-[0.3em]">
          {t('Round {round} · Reveal {n} of {total}', { round: room.current_round, n: idx + 1, total: ordered.length })}
        </p>
        <p className="font-display text-2xl text-brand-text mt-4 max-w-4xl mx-auto leading-snug">
          {tn('{a} while {b}', {
            a, b: <span className="text-brand-text">{b}</span>,
          })}
        </p>
        <h2 className="font-display text-4xl font-bold text-brand-text mt-12">
          {author.display_name}
        </h2>
        <ol className="mt-8 space-y-5 text-left">
          {[current?.step1_text, current?.step2_text, current?.step3_text].map((stepText, i) => (
            <li key={i} className="bg-game-three-step-light border-2 border-game-three-step-dark
                                   rounded-3xl px-8 py-6 flex gap-6 items-start">
              <span className="font-display text-4xl font-black text-game-three-step-dark shrink-0">
                {i + 1}.
              </span>
              <span className="font-display text-3xl text-brand-text leading-snug">
                {stepText ?? '…'}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </TvShell>
  )
}

function TvPhase4Voting({ snap }: { snap: ThreeStepRoomSnapshot }) {
  const t = useT()
  const { room, players, progress } = snap
  // Votes are RLS-public so we could count from `votes` directly, but
  // sourcing through the progress RPC keeps all three phase counters
  // consistent (and ticks via the same room.progress_tick path).
  const cast = new Set(progress.phase4VotedIds)
  return (
    <TvShell roomCode={room.code}>
      <div className="text-center">
        <p className="font-display text-2xl text-brand-muted uppercase tracking-[0.3em]">
          {t('Round {n} · voting', { n: room.current_round })}
        </p>
        <h1 className="font-display text-7xl font-black text-brand-text mt-6">{t('Pick your favourite')}</h1>
        <p className="font-display text-3xl text-brand-muted mt-8">
          {t('{n} / {total} voted', { n: cast.size, total: players.length })}
        </p>
        <PlayerStrip players={players} doneIds={cast} className="mt-16" />
      </div>
    </TvShell>
  )
}

function TvBetweenRounds({ snap }: { snap: ThreeStepRoomSnapshot }) {
  const t = useT()
  const { room, players } = snap
  const ranked = players.slice().sort((a, b) => b.total_votes - a.total_votes)
  return (
    <TvShell roomCode={room.code}>
      <div className="text-center">
        <p className="font-display text-2xl text-brand-muted uppercase tracking-[0.3em]">
          {t('End of round {n}', { n: room.current_round })}
        </p>
        <h1 className="font-display text-7xl font-black text-brand-text mt-6">
          {t('Standings')}
        </h1>
        <Standings players={ranked} className="mt-12" />
      </div>
    </TvShell>
  )
}

function TvResults({ snap }: { snap: ThreeStepRoomSnapshot }) {
  const t = useT()
  const { room, players } = snap
  const ranked = players.slice().sort((a, b) => b.total_votes - a.total_votes)
  const winner = ranked[0]
  return (
    <TvShell roomCode={room.code}>
      <div className="text-center">
        <p className="font-display text-2xl text-brand-muted uppercase tracking-[0.3em]">{t('Final results')}</p>
        <h1 className="font-display text-[6rem] leading-none font-black text-game-three-step-dark mt-8">
          {t('{name} wins', { name: winner?.display_name ?? '…' })}
        </h1>
        <p className="font-display text-4xl text-brand-muted mt-6">
          {t('{n} votes', { n: winner?.total_votes ?? 0 })}
        </p>
        <Standings players={ranked} className="mt-16" />
      </div>
    </TvShell>
  )
}

// ─── Reusable bits ───────────────────────────────────────────────────

function TvShell({ roomCode, children }: { roomCode: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-brand-bg text-brand-text">
      <header className="px-12 py-8 flex items-center justify-between">
        <span className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-muted">
          THREE STEP SOLUTION
        </span>
        <span className="font-mono text-2xl font-bold text-brand-text tracking-[0.2em]">
          {roomCode}
        </span>
      </header>
      <main className="flex-1 flex items-center justify-center px-12 pb-12">
        {children}
      </main>
    </div>
  )
}

function PlayerStrip({
  players, doneIds, className,
}: {
  players: { id: string; display_name: string; is_ai: boolean }[]
  doneIds?: Set<string>
  className?: string
}) {
  return (
    <ul className={`flex flex-wrap justify-center gap-4 ${className ?? ''}`}>
      {players.map(p => {
        const isDone = doneIds?.has(p.id)
        return (
          <li key={p.id}
              className={
                'px-6 py-4 rounded-2xl border-2 font-display text-2xl flex items-center gap-3 ' +
                (isDone
                  ? 'bg-game-three-step-light border-game-three-step-dark text-brand-text'
                  : 'bg-white border-brand-border text-brand-muted')
              }>
            {p.is_ai && <span className="text-xl">🤖</span>}
            <span className="font-semibold">{p.display_name}</span>
            {isDone && <span className="text-game-three-step-dark">✓</span>}
          </li>
        )
      })}
    </ul>
  )
}

function Standings({
  players, className,
}: {
  players: { id: string; display_name: string; total_votes: number; is_ai: boolean }[]
  className?: string
}) {
  const t = useT()
  return (
    <ol className={`max-w-2xl mx-auto space-y-3 text-left ${className ?? ''}`}>
      {players.map((p, i) => (
        <li key={p.id} className="bg-white border-2 border-brand-border rounded-2xl
                                  px-8 py-5 flex items-center justify-between">
          <span className="flex items-center gap-4">
            <span className="font-display text-3xl font-black text-game-three-step-dark w-10">
              {i + 1}.
            </span>
            <span className="font-display text-2xl text-brand-text">
              {p.is_ai && '🤖 '}{p.display_name}
            </span>
          </span>
          <span className="font-display text-2xl font-bold text-brand-text">
            {p.total_votes === 1 ? t('1 vote') : t('{n} votes', { n: p.total_votes })}
          </span>
        </li>
      ))}
    </ol>
  )
}

function FullMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg text-brand-text p-12">
      <div className="text-center max-w-3xl">
        <h1 className="font-display text-5xl font-bold mb-4">{title}</h1>
        <p className="text-xl text-brand-muted">{body}</p>
      </div>
    </div>
  )
}
